package br.com.htms.inspeccaocampo.shared.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.platform.PlatformServices

private data class StrokeLine(val points: List<Offset>)

private val SignatureYellow = Color(0xFFFFF59D)

@Composable
fun SignaturePadScreen(
    titulo: String,
    onCancelar: () -> Unit,
    onConfirmar: (String) -> Unit
) {
    val strokes = remember { mutableStateListOf<StrokeLine>() }
    var currentStroke by remember { mutableStateOf<List<Offset>>(emptyList()) }
    var canvasSize by remember { mutableStateOf(IntSize.Zero) }

    Surface(
        modifier = Modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.surface
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 20.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text(titulo, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
            Text(
                "Desenhe sua assinatura no campo abaixo.",
                style = MaterialTheme.typography.bodyMedium
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .background(Color.White, RoundedCornerShape(8.dp))
                    .border(1.dp, MaterialTheme.colorScheme.outline, RoundedCornerShape(8.dp))
                    .onSizeChanged { canvasSize = it }
                    .pointerInput(Unit) {
                        detectDragGestures(
                            onDragStart = { offset -> currentStroke = listOf(offset) },
                            onDrag = { change, _ ->
                                currentStroke = currentStroke + change.position
                                change.consume()
                            },
                            onDragEnd = {
                                if (currentStroke.isNotEmpty()) {
                                    strokes.add(StrokeLine(currentStroke))
                                    currentStroke = emptyList()
                                }
                            },
                            onDragCancel = {
                                if (currentStroke.isNotEmpty()) {
                                    strokes.add(StrokeLine(currentStroke))
                                    currentStroke = emptyList()
                                }
                            }
                        )
                    }
            ) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val paint = Stroke(width = 5f, cap = StrokeCap.Round, join = StrokeJoin.Round)
                    (strokes + StrokeLine(currentStroke)).forEach { stroke ->
                        if (stroke.points.size < 2) return@forEach
                        val path = Path()
                        path.moveTo(stroke.points.first().x, stroke.points.first().y)
                        stroke.points.drop(1).forEach { path.lineTo(it.x, it.y) }
                        drawPath(path, Color.Black, style = paint)
                    }
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
                verticalAlignment = Alignment.CenterVertically
            ) {
                OutlinedButton(onClick = {
                    strokes.clear()
                    currentStroke = emptyList()
                }) { Text("Limpar") }
                OutlinedButton(onClick = onCancelar) { Text("Cancelar") }
                Button(
                    onClick = {
                        val pairs = strokes.map { line ->
                            line.points.map { it.x to it.y }
                        }
                        val w = canvasSize.width.coerceAtLeast(1)
                        val h = canvasSize.height.coerceAtLeast(1)
                        val b64 = PlatformServices.encodeSignaturePngBase64(pairs, w, h)
                        if (b64 != null) onConfirmar(b64)
                    },
                    enabled = strokes.isNotEmpty()
                ) { Text("Confirmar") }
            }
        }
    }
}

@Composable
fun SignatureField(
    label: String,
    assinaturaToken: String,
    erro: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .background(SignatureYellow.copy(alpha = 0.55f), RoundedCornerShape(8.dp))
            .border(1.dp, SignatureYellow, RoundedCornerShape(8.dp))
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(label, style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(90.dp)
                .padding(top = 8.dp)
                .background(Color.White, RoundedCornerShape(4.dp))
                .border(1.dp, MaterialTheme.colorScheme.outline, RoundedCornerShape(4.dp))
                .clickable(onClick = onClick),
            contentAlignment = Alignment.Center
        ) {
            Text(
                if (assinaturaToken.isNotBlank()) "Assinado — toque para refazer"
                else "Toque para assinar",
                style = MaterialTheme.typography.bodySmall
            )
        }
        erro?.let {
            Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
        }
    }
}
